import type { GapsTrend } from '../../types';

interface GapTrendChartProps {
  trends: GapsTrend[];
}

export function GapTrendChart({ trends }: GapTrendChartProps) {
  if (trends.length === 0) {
    return <p className="text-wolf-500 text-sm">No trend data available</p>;
  }

  const maxTotal = Math.max(...trends.map(t => t.total), 1);
  const chartHeight = 160;
  const barWidth = 100 / trends.length;

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-wolf-300">Gap Trends Over Time</h3>
      <div className="relative" style={{ height: chartHeight + 40 }}>
        {/* Y-axis labels */}
        <div className="absolute left-0 top-0 bottom-8 w-8 flex flex-col justify-between text-xs text-wolf-500">
          <span>{maxTotal}</span>
          <span>{Math.round(maxTotal / 2)}</span>
          <span>0</span>
        </div>

        {/* Chart area */}
        <div className="ml-10 h-full">
          <div
            className="flex items-end gap-1 border-b border-l border-wolf-700"
            style={{ height: chartHeight }}
          >
            {trends.map((trend) => {
              const height = (trend.total / maxTotal) * 100;
              const highHeight = (trend.high / trend.total) * height || 0;
              const mediumHeight = (trend.medium / trend.total) * height || 0;
              const lowHeight = (trend.low / trend.total) * height || 0;

              return (
                <div
                  key={trend.period}
                  className="flex flex-col justify-end"
                  style={{ width: `${barWidth}%`, height: '100%' }}
                  title={`${trend.period}: ${trend.total} gaps`}
                >
                  {/* Stacked bar */}
                  <div
                    className="w-full bg-red-500 transition-all duration-300"
                    style={{ height: `${highHeight}%` }}
                  />
                  <div
                    className="w-full bg-yellow-500 transition-all duration-300"
                    style={{ height: `${mediumHeight}%` }}
                  />
                  <div
                    className="w-full bg-green-500 transition-all duration-300"
                    style={{ height: `${lowHeight}%` }}
                  />
                </div>
              );
            })}
          </div>

          {/* X-axis labels */}
          <div className="flex mt-1">
            {trends.map((trend, i) => (
              <div
                key={trend.period}
                className="text-xs text-wolf-500 text-center"
                style={{ width: `${barWidth}%` }}
              >
                {i % 2 === 0 ? trend.period.slice(5) : ''}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex gap-4 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-red-500 rounded" />
          <span className="text-wolf-400">High</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-yellow-500 rounded" />
          <span className="text-wolf-400">Medium</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-green-500 rounded" />
          <span className="text-wolf-400">Low</span>
        </div>
      </div>
    </div>
  );
}
